<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from '@/composables/useToast'
import { stockDailyApi, tradeCalendarApi } from '@/api/stock'
import PageHero from '@/components/common/PageHero.vue'
import BasePagination from '@/components/common/BasePagination.vue'
import { useViewport } from '@/composables/useViewport'
import { useFixedRows } from '@/composables/useFixedRows'
import { formatAmount, formatTurnover } from '@/utils/formatters'

const route = useRoute()
const router = useRouter()
const { isMobile } = useViewport()

const limitType = computed(() => (route.meta.limitType === 'down' ? 'down' : 'up'))
const isUp = computed(() => limitType.value === 'up')

const latestTradeDate = ref('')
const queryDate = ref('')
const loading = ref(false)

const filter = ref({
  symbol: '',
  excludeSt: true,
  excludeCyb: true,
  excludeKcb: true,
  excludeBse: true
})

const data = ref(null)
const pageNum = ref(1)
const pageSize = ref(10)

function fmtPct (val) {
  if (val === null || val === undefined) return '—'
  const v = parseFloat(val)
  return (v >= 0 ? '+' : '') + v.toFixed(2) + '%'
}

function fmtNum (val) {
  if (val === null || val === undefined) return '—'
  return parseFloat(val).toFixed(2)
}

function chgClass (val) {
  if (val === null || val === undefined) return ''
  const v = parseFloat(val)
  if (v > 0) return 'up'
  if (v < 0) return 'down'
  return ''
}

// 连板高度：N天M板（含短板窗口，连续3天无涨停则重置）
// 1天1板 → 首板；否则显示 N天M板（如 3天2板、7天7板）
function fmtStreak (item) {
  const m = item.streak || 1
  const n = item.streak_days || m
  if (m <= 1 && n <= 1) return '首板'
  return `${n}天${m}板`
}

// 根据页面类型（涨停/跌停）取对应列表
const mergedList = computed(() => {
  const list = isUp.value
    ? (data.value?.limit_up_list || [])
    : (data.value?.limit_down_list || [])
  return list.map(item => ({ ...item, limitType: limitType.value }))
})

const total = computed(() => mergedList.value.length)

const countTotal = computed(() => (isUp.value
  ? (data.value?.limit_up_count || 0)
  : (data.value?.limit_down_count || 0)))

// ---------- 涨停：连板分组（参考 8.3 tab-slider） ----------
const groups = [
  { key: 1, label: '一板', desc: '今日首板' },
  { key: 2, label: '二板', desc: '连续2天涨停' },
  { key: 3, label: '三板', desc: '连续3天涨停' },
  { key: 4, label: '四板', desc: '连续4天涨停' },
  { key: 5, label: '更高板', desc: '5板及以上' }
]

const grouped = computed(() => {
  const map = {}
  for (const g of groups) {
    map[g.key] = []
  }
  for (const item of mergedList.value) {
    const s = item.streak || 1
    const key = s >= 5 ? 5 : s
    if (map[key]) {
      map[key].push(item)
    }
  }
  return map
})

const activeGroup = ref(1)
const pageState = ref({ 1: 1, 2: 1, 3: 1, 4: 1, 5: 1 })

watch(() => data.value, () => {
  pageState.value = { 1: 1, 2: 1, 3: 1, 4: 1, 5: 1 }
  if (!isUp.value || !mergedList.value.length) return
  nextTick(() => {
    if (!grouped.value[activeGroup.value]?.length) {
      const first = groups.find(g => grouped.value[g.key]?.length > 0)
      if (first) activeGroup.value = first.key
    }
  })
})

// 涨停分组当前列表
const currentGroup = computed(() => grouped.value[activeGroup.value] || [])
const groupPageNum = computed(() => pageState.value[activeGroup.value] || 1)
const groupPaginated = computed(() => {
  const start = (groupPageNum.value - 1) * pageSize.value
  return currentGroup.value.slice(start, start + pageSize.value)
})
const groupPadded = useFixedRows(groupPaginated, { count: 10 })

// 跌停：单表分页
const pagedList = computed(() => {
  const start = (pageNum.value - 1) * pageSize.value
  return mergedList.value.slice(start, start + pageSize.value)
})
const paddedList = useFixedRows(pagedList, { count: 10 })

// tab 滑块指示器
const tabRefs = ref([])
const indicatorStyle = ref({ width: '0px', transform: 'translateX(0px)' })

function updateIndicator () {
  const idx = groups.findIndex(g => g.key === activeGroup.value)
  const el = tabRefs.value[idx]
  if (el) {
    indicatorStyle.value = {
      width: `${el.offsetWidth}px`,
      transform: `translateX(${el.offsetLeft}px)`
    }
  }
}

watch(activeGroup, () => {
  nextTick(updateIndicator)
})
onMounted(() => nextTick(updateIndicator))

function setTabRef (el, idx) {
  if (el) tabRefs.value[idx] = el
}

function handleGroupPageChange (newPage) {
  pageState.value[activeGroup.value] = newPage
}

function getQueryParams () {
  const params = {}
  const trimmed = filter.value.symbol?.trim()
  if (trimmed) {
    params.keyword = trimmed
  }
  params.exclude_st = filter.value.excludeSt
  params.exclude_cyb = filter.value.excludeCyb
  params.exclude_kcb = filter.value.excludeKcb
  params.exclude_bse = filter.value.excludeBse
  return params
}

async function handleSearch () {
  if (!queryDate.value) {
    toast.warning('请选择交易日期')
    return
  }
  loading.value = true
  pageNum.value = 1
  try {
    const res = await stockDailyApi.getLimitStocks(queryDate.value, getQueryParams())
    data.value = res || null
  } catch (error) {
    console.error('加载涨停跌停数据失败:', error)
    data.value = null
  } finally {
    loading.value = false
  }
}

function handleReset () {
  filter.value.symbol = ''
  filter.value.excludeSt = true
  filter.value.excludeCyb = true
  filter.value.excludeKcb = true
  filter.value.excludeBse = true
  handleSearch()
}

function handlePageChange (newPage) {
  pageNum.value = newPage
}

function handlePageSizeChange (newSize) {
  pageSize.value = newSize
  pageNum.value = 1
}

function handleRowDblClick (item) {
  if (!item?.symbol) return
  router.push({
    name: 'stock-kline',
    params: { symbol: item.symbol },
    query: { name: item.name || '' }
  })
}

async function loadLatestTradeDate () {
  try {
    const date = await tradeCalendarApi.getLatest()
    if (date) {
      latestTradeDate.value = date
      queryDate.value = date
      return date
    }
  } catch (error) {
    console.error('获取最近交易日失败:', error)
  }
  queryDate.value = ''
  latestTradeDate.value = ''
  return ''
}

onMounted(async () => {
  const date = await loadLatestTradeDate()
  if (date) {
    await handleSearch()
  }
})
</script>

<template>
  <div class="limit-stocks page">
    <PageHero />

    <div class="filter-section">
      <div class="filter-fields">
        <label class="field">
          <span class="field-label">交易日</span>
          <input v-model="queryDate" type="date" @change="handleSearch" />
        </label>

        <label class="field">
          <span class="field-label">代码</span>
          <input v-model="filter.symbol" type="text" placeholder="600519 / 茅台" @keyup.enter="handleSearch" />
        </label>

        <div class="field exclude-field">
          <span class="field-label">板块过滤</span>
          <div class="exclude-chips">
            <label><input v-model="filter.excludeSt" type="checkbox" @change="handleSearch" /><span>排除 ST</span></label>
            <label><input v-model="filter.excludeCyb" type="checkbox" @change="handleSearch" /><span>排除创业板</span></label>
            <label><input v-model="filter.excludeKcb" type="checkbox" @change="handleSearch" /><span>排除科创板</span></label>
            <label><input v-model="filter.excludeBse" type="checkbox" @change="handleSearch" /><span>排除北交所</span></label>
          </div>
        </div>
      </div>

      <div class="filter-actions">
        <button class="btn-link" @click="handleReset">重置</button>
        <button class="btn-primary" @click="handleSearch">检索</button>
      </div>
    </div>

    <div class="result-bar">
      <span class="result-item" :class="isUp ? 'up' : 'down'">{{ countTotal }} {{ isUp ? '涨停' : '跌停' }}</span>
      <span class="result-sep">·</span>
      <span class="result-item">{{ total }} 合计</span>
      <span v-if="data?.prev_trade_date" class="result-sub">前收日 {{ data.prev_trade_date }}</span>
    </div>

    <div v-if="loading" class="state loading">加载中…</div>
    <div v-else-if="total === 0" class="state empty">{{ isUp ? '当日无涨停个股' : '当日无跌停个股' }}</div>

    <!-- ============ 涨停：连板分组（参考 8.3） ============ -->
    <template v-else-if="isUp">
      <div class="tab-slider">
        <div class="tab-list">
          <div
            v-for="(g, idx) in groups"
            :key="g.key"
            class="tab-item"
            :class="{ active: activeGroup === g.key, empty: !grouped[g.key]?.length }"
            @click="grouped[g.key]?.length ? activeGroup = g.key : null"
            :ref="el => setTabRef(el, idx)"
          >
            <div class="tab-main">
              <span class="tab-label">{{ g.label }}</span>
              <span class="tab-count">{{ grouped[g.key]?.length || 0 }}</span>
            </div>
            <div class="tab-desc">{{ g.desc }}</div>
          </div>
          <div class="tab-indicator" :style="indicatorStyle"></div>
        </div>
      </div>

      <div class="table-section">
        <div v-if="!currentGroup.length" class="state empty">该分组暂无数据</div>
        <table v-else class="stock-table">
          <thead>
            <tr>
              <th class="th-left">代码</th>
              <th class="th-left">名称</th>
              <th>连板</th>
              <th>收盘</th>
              <th>涨跌幅</th>
              <th>前收</th>
              <th>涨停价</th>
              <th>换手率</th>
              <th>成交额</th>
              <th>振幅</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in groupPadded"
              :key="item === null ? `empty-${index}` : (item.symbol || index)"
              class="data-row"
              :class="{ 'empty-row': item === null }"
              @dblclick="item && handleRowDblClick(item)"
            >
              <template v-if="item">
                <td class="code">{{ item.symbol }}</td>
                <td class="name">{{ item.name }}</td>
                <td class="up">{{ fmtStreak(item) }}</td>
                <td :class="chgClass(item.pct_chg)">{{ fmtNum(item.close) }}</td>
                <td :class="chgClass(item.pct_chg)">{{ fmtPct(item.pct_chg) }}</td>
                <td>{{ fmtNum(item.prev_close) }}</td>
                <td class="up">{{ fmtNum(item.limit_up_price) }}</td>
                <td>{{ formatTurnover(item.turnover) }}</td>
                <td>{{ formatAmount(item.amount) }}</td>
                <td>{{ fmtNum(item.amplitude) }}%</td>
              </template>
              <template v-else>
                <td v-for="col in 10" :key="`empty-${index}-${col}`">&nbsp;</td>
              </template>
            </tr>
          </tbody>
        </table>
        <BasePagination
          v-if="currentGroup.length > 0"
          :page-num="groupPageNum"
          :page-size="pageSize"
          :total="currentGroup.length"
          @update:page-num="handleGroupPageChange"
          @update:page-size="handlePageSizeChange"
        />
      </div>
    </template>

    <!-- ============ 跌停：单表 ============ -->
    <template v-else>
      <div class="table-section">
        <table class="stock-table">
          <thead>
            <tr>
              <th class="th-left">代码</th>
              <th class="th-left">名称</th>
              <th>收盘</th>
              <th>涨跌幅</th>
              <th>前收</th>
              <th>跌停价</th>
              <th>换手率</th>
              <th>成交额</th>
              <th>振幅</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in paddedList"
              :key="item === null ? `empty-${index}` : (item.symbol || index)"
              class="data-row"
              :class="{ 'empty-row': item === null }"
              @dblclick="item && handleRowDblClick(item)"
            >
              <template v-if="item">
                <td class="code">{{ item.symbol }}</td>
                <td class="name">{{ item.name }}</td>
                <td :class="chgClass(item.pct_chg)">{{ fmtNum(item.close) }}</td>
                <td :class="chgClass(item.pct_chg)">{{ fmtPct(item.pct_chg) }}</td>
                <td>{{ fmtNum(item.prev_close) }}</td>
                <td class="down">{{ fmtNum(item.limit_down_price) }}</td>
                <td>{{ formatTurnover(item.turnover) }}</td>
                <td>{{ formatAmount(item.amount) }}</td>
                <td>{{ fmtNum(item.amplitude) }}%</td>
              </template>
              <template v-else>
                <td v-for="col in 9" :key="`empty-${index}-${col}`">&nbsp;</td>
              </template>
            </tr>
          </tbody>
        </table>
        <BasePagination
          v-if="total > 0"
          :page-num="pageNum"
          :page-size="pageSize"
          :total="total"
          @update:page-num="handlePageChange"
          @update:page-size="handlePageSizeChange"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px 28px 18px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4px 16px 12px;
  }
}

.filter-section {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 10px 0 12px;
  border-bottom: 1px dashed var(--rule);
  flex-shrink: 0;
}

.filter-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  flex: 1;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .field-label {
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-faint);
  }

  > input {
    padding: 5px 0;
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--rule);
    border-radius: 0;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-primary);
    transition: border-color 0.2s;
    min-width: 140px;

    &:focus {
      outline: none;
      border-bottom-color: var(--accent);
    }

    &::placeholder {
      color: var(--text-faint);
    }
  }
}

.exclude-field {
  flex: 1;
  min-width: 260px;
}

.exclude-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 2px;

  label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px;
    border: 1px solid var(--rule);
    border-radius: 999px;
    cursor: pointer;
    font-size: 11px;
    color: var(--text-muted);
    transition: all 0.18s;
    font-family: var(--font-body);

    &:hover {
      border-color: var(--text-faint);
      color: var(--text-secondary);
    }

    input[type='checkbox'] {
      width: 12px;
      height: 12px;
      margin: 0;
      accent-color: var(--accent);
    }

    &:has(input:checked) {
      background: var(--accent-subtle);
      border-color: rgba(59, 130, 246, 0.35);
      color: var(--text-primary);
    }
  }
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.btn-primary {
  padding: 6px 14px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: background 0.2s;

  &:hover { background: var(--accent-hover); }
}

.btn-link {
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 5px 0;
  transition: color 0.2s;

  &:hover { color: var(--text-primary); }
}

.result-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0 8px;
  font-family: var(--font-mono);
  font-size: 12px;
  flex-shrink: 0;

  .result-item {
    font-weight: 600;
    letter-spacing: 0.06em;

    &.up { color: var(--up); }
    &.down { color: var(--down); }
  }

  .result-sep {
    color: var(--text-faint);
    opacity: 0.5;
  }

  .result-sub {
    margin-left: auto;
    color: var(--text-faint);
    font-size: 11px;
  }
}

/* 横线滑块 Tab（参考 8.3） */
.tab-slider {
  flex-shrink: 0;
  z-index: 10;
  background: var(--bg-primary);
  border-bottom: 1px solid var(--rule);
  padding: 0 16px;
}

.tab-list {
  position: relative;
  display: flex;
  gap: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tab-item {
  flex-shrink: 0;
  padding: 12px 20px 14px;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  position: relative;

  &.empty {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:not(.empty):hover {
    color: var(--text-primary);
  }

  &.active {
    color: var(--accent);
  }
}

.tab-main {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.tab-label {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 700;
}

.tab-count {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.tab-item.active .tab-count {
  background: var(--accent);
  color: white;
}

.tab-desc {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 2px;
  white-space: nowrap;
}

.tab-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: var(--accent);
  border-radius: 2px 2px 0 0;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.table-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-top: 10px;
}

.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-mono);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  table-layout: fixed;
  flex: 1;
  min-height: 0;

  th,
  td {
    padding: 11px 14px;
    text-align: right;
    border-bottom: 1px solid var(--rule);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:first-child,
    &:nth-child(2) {
      text-align: left;
    }
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--bg-primary);
    font-family: var(--font-mono);
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-faint);
    border-bottom: 1px solid var(--rule);

    &.th-left { text-align: left; }
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: var(--bg-tertiary); }

    &.empty-row {
      cursor: default;

      &:hover { background: transparent; }

      td {
        color: transparent;
        pointer-events: none;
        border-bottom-color: transparent;
      }
    }
  }

  td {
    color: var(--text-primary);
  }

  .code {
    text-align: left;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: 0.04em;
  }

  .name {
    text-align: left;
    font-family: var(--font-body);
    font-weight: 500;
    color: var(--text-primary);
  }

  .up { color: var(--up); }
  .down { color: var(--down); }
}

.state {
  padding: 32px;
  text-align: center;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-faint);

  &.empty {
    color: var(--text-muted);
  }
}

@media (max-width: 768px) {
  .page {
    overflow: visible;
  }

  .filter-section {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .field {
    width: 100%;

    > input {
      min-width: auto;
      width: 100%;
      box-sizing: border-box;
    }
  }

  .exclude-field {
    min-width: auto;
  }

  .exclude-chips {
    label {
      flex: 1;
      justify-content: center;
      min-height: var(--touch-target);
    }
  }

  .filter-actions {
    justify-content: stretch;

    button {
      flex: 1;
      min-height: var(--touch-target);
    }
  }

  .tab-list {
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: block;
      height: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--text-faint);
      border-radius: 2px;
    }
  }

  .tab-item {
    padding: 10px 14px 12px;
  }

  .tab-desc {
    font-size: 10px;
  }

  .stock-table {
    th,
    td {
      white-space: normal;
      padding: 8px 10px;
      font-size: 12px;
    }
  }

  .table-section {
    overflow-x: auto;
  }
}
</style>
